import { useEffect, useState } from "react";
import { Clock, Hourglass, Timer } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";

const FALLBACK_IDLE_MINUTES = 30;
const WARN_LEAD_MS  =  2 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click"];
const SESSION_KEY = "admin_session_started_at";

const fmtHMS = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
        : `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * Small monitoring pill for the Admin Dashboard — surfaces:
 *   • wall-clock local time (H:MM:SS AM/PM)
 *   • how long the current session has been signed in
 *   • how long until the 30-min idle auto-logoff kicks in (warns amber inside
 *     the 2-min warning window, red in the final 30 seconds).
 */
export default function SessionMonitor() {
    const { user } = useAuth();
    const idleMinutes = Number(user?.idle_timeout_minutes) > 0 ? Number(user.idle_timeout_minutes) : FALLBACK_IDLE_MINUTES;
    const IDLE_LIMIT_MS = idleMinutes * 60 * 1000;
    const [now, setNow] = useState(Date.now());
    const [lastActivity, setLastActivity] = useState(Date.now());
    const [sessionStart, setSessionStart] = useState(() => {
        const stored = Number(localStorage.getItem(SESSION_KEY));
        if (stored && Number.isFinite(stored) && stored > 0) return stored;
        const fresh = Date.now();
        localStorage.setItem(SESSION_KEY, String(fresh));
        return fresh;
    });

    // Wall-clock + session-duration tick (1 Hz).
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    // Mirror IdleTimeout's activity events so we can render "time until auto
    // logoff" without introducing a coupling between the two components.
    useEffect(() => {
        let lastWrite = 0;
        const bump = () => {
            const t = Date.now();
            if (t - lastWrite < 2_000) return;  // throttle to 2s granularity
            lastWrite = t;
            setLastActivity(t);
        };
        ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
        return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
    }, []);

    // If a fresh admin session was created elsewhere (e.g. impersonation),
    // pick it up so the counter doesn't lag a login.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === SESSION_KEY && e.newValue) {
                const n = Number(e.newValue);
                if (Number.isFinite(n) && n > 0) setSessionStart(n);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const localTime = new Date(now).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
    const sessionElapsed = now - sessionStart;
    const idleMsRemaining = Math.max(0, IDLE_LIMIT_MS - (now - lastActivity));
    const inWarning = idleMsRemaining <= WARN_LEAD_MS;
    const inDanger  = idleMsRemaining <= 30_000;

    const timerTone = inDanger
        ? "text-rose-600 bg-rose-50 border-rose-200"
        : inWarning
            ? "text-amber-700 bg-amber-50 border-amber-200"
            : "text-slate-600 bg-white border-slate-200";

    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="session-monitor">
            <div className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-slate-200 bg-white hash-mono text-[11px] text-slate-700" title={new Date(now).toString()} data-testid="session-clock">
                <Clock size={12} weight="duotone" className="text-civic-600" />
                <span>{localTime}</span>
            </div>
            <div className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-slate-200 bg-white hash-mono text-[11px] text-slate-700" title={`Signed in at ${new Date(sessionStart).toLocaleString()}`} data-testid="session-duration">
                <Hourglass size={12} weight="duotone" className="text-emerald-600" />
                <span>on {fmtHMS(sessionElapsed)}</span>
            </div>
            <div className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-md border hash-mono text-[11px] ${timerTone}`} title="Time until auto-logoff (resets on activity)" data-testid="session-idle">
                <Timer size={12} weight={inWarning ? "fill" : "duotone"} />
                <span>auto-off in {fmtHMS(idleMsRemaining)}</span>
            </div>
        </div>
    );
}

/**
 * Helper for the AuthContext to reset the session-start timestamp on a fresh
 * login, and to clear it on logout so the next login starts from zero.
 */
export function stampSessionStart() {
    localStorage.setItem(SESSION_KEY, String(Date.now()));
}
export function clearSessionStart() {
    localStorage.removeItem(SESSION_KEY);
}
